/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedCurrency } from '../../../../ducks/currency.duck';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { STUDENT } from '../../../../util/types';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';
import { switchLanguage } from '../TopbarDesktop/LanguageMenu/LanguageMenu';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const mobileCurrency = useSelector(state => state.currency.selectedCurrency);

  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return (typeof window !== 'undefined' && localStorage.getItem('selectedLanguage')) || 'en';
  });

  useEffect(() => {
    const onLanguageChange = e => {
      const next = e?.detail?.locale === 'ar' ? 'ar' : 'en';
      setCurrentLanguage(next);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('languageChange', onLanguageChange);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('languageChange', onLanguageChange);
      }
    };
  }, []);

  const handleMobileLanguageChange = lang => {
    setCurrentLanguage(lang);
    switchLanguage({
      language: lang,
      history,
      location,
      currentPage,
      currentUser,
      dispatch,
    });
  };

  const handleMobileCurrencyChange = (curr) => {
    dispatch(setSelectedCurrency(curr));
  };

  const languageSelectorMarkup = (
    <div className={css.mobileLanguageSelectorContainer}>
      <span className={css.languageSelectorLabel}>
        <FormattedMessage id="TopbarMobileMenu.languageLabel" defaultMessage="Language:" />
      </span>
      <div className={css.mobileLanguageButtons}>
        <button
          key="en"
          type="button"
          className={classNames(css.mobileLanguageBtn, { [css.activeLanguage]: currentLanguage === 'en' })}
          onClick={() => handleMobileLanguageChange('en')}
        >
          English
        </button>
        <button
          key="ar"
          type="button"
          className={classNames(css.mobileLanguageBtn, { [css.activeLanguage]: currentLanguage === 'ar' })}
          onClick={() => handleMobileLanguageChange('ar')}
        >
          العربية
        </button>
      </div>
    </div>
  );

  const currencySelectorMarkup = (
    <div className={css.mobileCurrencySelectorContainer}>
      <span className={css.currencySelectorLabel}>
        <FormattedMessage id="TopbarMobileMenu.currencyLabel" defaultMessage="Currency:" />
      </span>
      <div className={css.mobileCurrencyButtons}>
        {['AED', 'USD', 'EUR'].map(curr => (
          <button
            key={curr}
            type="button"
            className={classNames(css.mobileCurrencyBtn, { [css.activeCurrency]: mobileCurrency === curr })}
            onClick={() => handleMobileCurrencyChange(curr)}
          >
            {curr}
          </button>
        ))}
      </div>
    </div>
  );

  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = !isAuthenticated && showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>
          <NamedLink name="SearchPage" className={css.navigationLink}>
            <FormattedMessage id="Footer.platform.findTutor" />
          </NamedLink>
          <NamedLink name="SubscriptionPlan" className={css.navigationLink}>
           <FormattedMessage id="Footer.payments.pricing" />
          </NamedLink>
          <a href="/p/become-a-tutor" className={css.navigationLink}>
            <FormattedMessage id="Footer.platform.becomeTutor" />
          </a>
          <a href="/p/contact-us" className={css.navigationLink}>
              <FormattedMessage id="Footer.support.contact" />
          </a>
          {languageSelectorMarkup}
          {currencySelectorMarkup}
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{createListingsLinkMaybe}</div>
      </nav>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink
      className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
      name="ManageListingsPage"
    >
      <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
    </NamedLink>
  ) : null;
  const isAdmin = currentUser?.attributes?.profile?.protectedData?.isAdmin;
  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const isStudent = userType === STUDENT;
  const isFamilyChild = currentUser?.attributes?.profile?.publicData?.isFamilyChild === true;
  const isFamilyParent = currentUser?.attributes?.profile?.publicData?.isFamilyParent === true;
  const isIndependentStudent = isStudent && !isFamilyChild && !isFamilyParent;
  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>

          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('SearchPage'))}
            name="SearchPage"
          >
            <FormattedMessage id="TopbarDesktop.findTutor" />
          </NamedLink>
          {isAdmin ?
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('AdminPanel'))}
              name="AdminPanel"
            >
              <FormattedMessage id="TopbarMobileMenu.adminPanelLink" />
            </NamedLink>
            : null}

          {isAdmin ?
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('BlogsPanel'))}
              name="NewBlogsPage"
            >
              {/* <span className={css.menuItemBorder} /> */}
              <FormattedMessage id="TopbarDesktop.blogPanelLink" defaultMessage='Create blog' />
            </NamedLink>
            : null}
          {isAdmin ?
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('MyBlogs'))}
              name="ManageListingsPage"
            >
              {/* <span className={css.menuItemBorder} /> */}
              <FormattedMessage id="TopbarDesktop.myBlogsLink" defaultMessage='My Blogs' />
            </NamedLink>
            : null}
          <NamedLink
            className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {!isFamilyChild && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('WalletPage'))}
              name="WalletPage"
            >
              <FormattedMessage id="TopbarDesktop.myWallet" />
            </NamedLink>
          )}
          {isStudent ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('MyLessonsPage'))}
              name="MyLessonsPage"
            >
              <FormattedMessage id="TopbarDesktop.meLessons" />
            </NamedLink>
          ) : null}
          {isIndependentStudent ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('MyProgressPage'))}
              name="MyProgressPage"
            >
              <FormattedMessage id="TopbarDesktop.myProgress" defaultMessage="My Progress" />
            </NamedLink>
          ) : null}
          {/* {manageListingsLinkMaybe} */}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
            params={{ tab: 'basic-info' }}
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          {!isFamilyChild && (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('WalletPage'))}
              name="WalletPage"
              to={{ search: '', hash: '#wallet-referral-section' }}
            >
              <FormattedMessage id="TopbarDesktop.referFirend" />
            </NamedLink>
          )}

        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        {languageSelectorMarkup}
        {currencySelectorMarkup}
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>{createListingsLinkMaybe}</div>
    </div>
  );
};

export default TopbarMobileMenu;
